'use client'

import React from 'react'
import { Task, TaskStatus } from '@/contexts/TaskMonitorContext'
import { CheckCircle2, XCircle, Loader2, Clock, X } from 'lucide-react'
import { Progress } from '@/shared/ui/progress'
import { Button } from '@/shared/ui/button'
import { cn } from '@/lib/utils'

interface TaskItemProps {
  task: Task
  onRemove: (id: string) => void
}

const statusConfig: Record<TaskStatus, { icon: React.ReactNode; color: string; bgColor: string }> = {
  pending: {
    icon: <Clock className="h-4 w-4" />,
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
  },
  processing: {
    icon: <Loader2 className="h-4 w-4 animate-spin" />,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
  },
  success: {
    icon: <CheckCircle2 className="h-4 w-4" />,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
  },
  error: {
    icon: <XCircle className="h-4 w-4" />,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
  },
}

const typeLabels: Record<Task['type'], string> = {
  url_analysis: 'URL 분석',
  file_upload: '파일 업로드',
  qa_creation: 'Q&A 생성',
  bulk_upload: '대량 업로드',
}

export function TaskItem({ task, onRemove }: TaskItemProps) {
  const config = statusConfig[task.status]
  const canRemove = task.status === 'success' || task.status === 'error'

  return (
    <div className={cn('rounded-lg border p-3 transition-all', config.bgColor)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <div className={cn('mt-0.5', config.color)}>
            {config.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">
                {typeLabels[task.type]}
              </span>
              {task.completedAt && (
                <span className="text-xs text-gray-400">
                  {formatElapsedTime(task.createdAt, task.completedAt)}
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-gray-900 truncate">
              {task.title}
            </p>
            {task.description && (
              <p className="text-xs text-gray-500 truncate">
                {task.description}
              </p>
            )}
            {task.error && (
              <p className="text-xs text-red-600 mt-1">
                {task.error}
              </p>
            )}
            {task.status === 'processing' && task.progress !== undefined && (
              <div className="mt-2">
                <Progress value={task.progress} className="h-1" />
              </div>
            )}
          </div>
        </div>
        {canRemove && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={() => onRemove(task.id)}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  )
}

function formatElapsedTime(start: Date, end: Date): string {
  const elapsed = end.getTime() - start.getTime()
  const seconds = Math.floor(elapsed / 1000)
  const minutes = Math.floor(seconds / 60)

  if (minutes > 0) {
    return `${minutes}분 ${seconds % 60}초`
  }
  return `${seconds}초`
}
